import { ChildAssetDTO } from '@api/assets/models'
import {
  Checkbox,
  Modal,
  SimpleTable,
  Spacer,
  StatusIndicator,
  Text,
} from '@components/common'
import { STATUS_MAP } from '@constants'
import { useState } from 'react'

interface Props {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  childAssets: ChildAssetDTO[]
  parentAssetID: string
  parentAssetDesc: string
  newStatus: string
  onSubmit: (selectedChildIDs: string[]) => void
  isLoadingSubmit: boolean
}

const ChildAssetStatusModal = ({
  isOpen,
  setIsOpen,
  childAssets,
  parentAssetID,
  parentAssetDesc,
  newStatus,
  onSubmit,
  isLoadingSubmit,
}: Props) => {
  const [selectedIDs, setSelectedIDs] = useState<string[]>([])

  const allSelected = childAssets.length > 0 && selectedIDs.length === childAssets.length

  const statusLabel = newStatus === 'A' ? 'activate' : 'deactivate'

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIDs([])
    } else {
      setSelectedIDs(childAssets.map(c => c.AssetID))
    }
  }

  function toggleChild(assetID: string) {
    setSelectedIDs(prev =>
      prev.includes(assetID) ? prev.filter(id => id !== assetID) : [...prev, assetID]
    )
  }

  function handleSubmit() {
    onSubmit(selectedIDs)
  }

  function handleClose() {
    setSelectedIDs([])
    setIsOpen(false)
  }

  const actionLabel =
    selectedIDs.length > 0
      ? `${newStatus === 'A' ? 'Activate' : 'Deactivate'} parent + ${selectedIDs.length} child${selectedIDs.length > 1 ? 'ren' : ''}`
      : `${newStatus === 'A' ? 'Activate' : 'Deactivate'} parent only`

  return (
    <Modal
      title={`${newStatus === 'A' ? 'Activate' : 'Deactivate'} ${parentAssetID} (${parentAssetDesc})`}
      isOpen={isOpen}
      close={handleClose}
      action={handleSubmit}
      actionLabel={actionLabel}
      intent={newStatus === 'N' ? 'danger' : 'default'}
      isActionLoading={isLoadingSubmit}
    >
      <Text p>
        Asset <b>{parentAssetID}</b> has {childAssets.length} child asset
        {childAssets.length > 1 ? 's' : ''}. Select which children to also {statusLabel}.
      </Text>

      <Spacer y="default" />

      <SimpleTable>
        <SimpleTable.Head>
          <SimpleTable.HeadRow>
            <SimpleTable.Header>
              <Checkbox
                label=""
                checked={allSelected}
                onChange={toggleSelectAll}
              />
            </SimpleTable.Header>
            <SimpleTable.Header>Asset ID</SimpleTable.Header>
            <SimpleTable.Header>Description</SimpleTable.Header>
            <SimpleTable.Header>Current Status</SimpleTable.Header>
          </SimpleTable.HeadRow>
        </SimpleTable.Head>
        <SimpleTable.Body>
          {childAssets.map(child => (
            <SimpleTable.Row key={child.AssetID}>
              <SimpleTable.Cell>
                <Checkbox
                  label=""
                  checked={selectedIDs.includes(child.AssetID)}
                  onChange={() => toggleChild(child.AssetID)}
                />
              </SimpleTable.Cell>
              <SimpleTable.Cell>{child.AssetID}</SimpleTable.Cell>
              <SimpleTable.Cell>{child.AS_DESC}</SimpleTable.Cell>
              <SimpleTable.Cell>
                <StatusIndicator
                  type={STATUS_MAP[child.AS_DIS_CD]?.type ?? 'default'}
                  label={child.StatusName}
                />
              </SimpleTable.Cell>
            </SimpleTable.Row>
          ))}
        </SimpleTable.Body>
      </SimpleTable>
    </Modal>
  )
}

export default ChildAssetStatusModal
